import React, { createContext, useEffect, useState } from 'react'
import { Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom'
import { GetInitialRootState, initialDataContext } from './helpers/ctxHelper'
import { Dashboard } from './pages/Dashboard'
import { SelectedLaw } from './pages/SelectedLaw'
import { Laws } from './pages/Laws'
import { ComplianceOfDemands } from './pages/Demand/ComplianceOfDemands'
import { IRootState } from './interfaces/IRootState'
import { IDataContext } from './interfaces/IDataContext'
import { handleMessage } from './helpers/stateHelper'
import { t } from './i18n/i18n'
import { DemandQuestion } from './pages/DemandQuestion'
import { SystemSettings } from '././pages/SystemSettings/SystemSettings'
import { SNIVersion } from './api/schemas/schema'
import AnswerOptionForm from './pages/Answer/AnswerOptionForm'
import DemandComplianceModuleDefinitionForm from './pages/SystemSettings/DemandComplianceModuleDefinitionForm'
import { clearScrollPosition } from './helpers/scrollPositionHelper'
import { CustomerRegister } from './pages/Customer/CustomerRegister'
import { api } from './helpers/apiHelper'
import { useMsal } from '@azure/msal-react'
import { msalInstance } from './Root'
import { Loader } from './components/common/spinner/Loader'
import { useLogoutTimer } from './helpers/logoutTimerHelper'
import { RouteErrorElement } from './components/common/error/RouteErrorElement'
export const DataContext = createContext(initialDataContext)
const App = () => {
   const [state, setRootState] = useState<IRootState>(GetInitialRootState())
   useLogoutTimer(() => msalInstance.logoutRedirect())
   const dataContextValue: IDataContext = {
      state,
      setRootState,
      handleMessage,
   }
   const { accounts } = useMsal()
   useEffect(() => {
      if (!state.accessToken) {
         ;(async () => {
            try {
               const msalResponse = await msalInstance.acquireTokenSilent({
                  scopes: ['offline_access', 'openid', 'profile', `api://${process.env.REACT_APP_CLIENT_ID}/api.scope`],
                  account: accounts[0],
               })

               setRootState((prev) => ({ ...prev, accessToken: msalResponse.accessToken }))
            } catch (error: any) {
               msalInstance.logoutRedirect()

               handleMessage(dataContextValue.setRootState, 'error', t('SomethingWentWrong'), error.message)
            }
         })()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [accounts])
   useEffect(() => {
      if (!state.accessToken) return
   }, [state.accessToken])
   useEffect(() => {
      if (state.accessToken) {
         ;(async () => {
            setRootState((prev) => ({ ...prev, initialLoading: true }))
            try {
               const [demandsRes, answerOptionsRes, demandModulesRes, clausesRes] = await Promise.all([
                  api(state.accessToken).demandList(),
                  api(state.accessToken).answerOptionList(),
                  api(state.accessToken).demandModuleList(),
                  api(state.accessToken).clauseList(),
               ])
               setRootState((prev) => ({
                  ...prev,
                  demands: demandsRes.data,
                  answerOptions: answerOptionsRes.data,
                  demandComplianceModuleDefinitions: demandModulesRes.data,
                  clauses: clausesRes.data,
               }))
            } catch (error: any) {
               handleMessage(setRootState, 'error', t('FetchFailed'), error.message)
            } finally {
               setRootState((prev) => ({ ...prev, initialLoading: false }))
            }
         })()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [state.accessToken])

   useEffect(() => {
      if (state.initialLoading === false) {
         ;(async () => {
            try {
               const [subjectsRes, orgSizesRes, orgTypesRes, leadershipRes, snisRes, processesRes] = await Promise.all([
                  api(state.accessToken).subjectList(),
                  api(state.accessToken).organisationSizeList(),
                  api(state.accessToken).organisationTypeList(),
                  api(state.accessToken).leadershipPerspectiveList(),
                  api(state.accessToken).getAllSnIs({ group: 'main group' }),
                  api(state.accessToken).processList(),
               ])
               const snis = snisRes.data.sort((a, b) => a.code - b.code)
               setRootState((prev) => ({
                  ...prev,
                  subjects: subjectsRes.data,
                  organisationSizes: orgSizesRes.data,
                  organisationTypes: orgTypesRes.data,
                  leadershipPerspectives: leadershipRes.data,
                  snis_2007: snis.filter((x) => x.version === SNIVersion.SNI2007),
                  snis_2025: snis.filter((x) => x.version === SNIVersion.SNI2025),
                  processes: processesRes.data,
               }))
               clearScrollPosition()
            } catch (error: any) {
               handleMessage(setRootState, 'error', t('FetchFailed'), error.message)
            }
         })()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [state.initialLoading])

   useEffect(() => {
      if (!state.accessToken) return
      const interval = setInterval(
         async () => {
            try {
               const response = await api(state.accessToken).verify()
               if (response.status === 200) return
            } catch (err) {
               msalInstance.logoutRedirect()
            }
         },
         10 * 60 * 1000 // 10 min
      )

      return () => clearInterval(interval)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [state.accessToken])

   const routes = [
      {
         path: '/',
         element: <Dashboard />,
         //   loader: <Spinner />,
      },
      {
         path: '/customer-register',
         element: <CustomerRegister />,
         //   loader: <Spinner />,
      },
      {
         path: '/law-import/:id',
         element: <SelectedLaw />,
         //   loader: <Spinner />,
      },
      {
         path: '/law-import',
         element: <Laws />,
         //   loader: <Spinner />,
      },
      {
         path: '/demands/:id',
         element: <DemandQuestion />,
         //   loader: <Spinner />,
      },
      {
         path: '/demands',
         element: <ComplianceOfDemands />,
         //   loader: <Spinner />,
      },
      {
         path: '/system-settings',
         element: <SystemSettings />,
         //   loader: <Spinner />,
      },
      {
         path: '/system-settings/answer-options/:id',
         element: <AnswerOptionForm />,
         //   loader: <Spinner />,
      },
      {
         path: '/system-settings/demand-compliance-modules/:id',
         element: <DemandComplianceModuleDefinitionForm />,
         //   loader: <Spinner />,
      },
   ]
   const router = createBrowserRouter([{ element: <Outlet />, errorElement: <RouteErrorElement />, children: routes }])
   return (
      <DataContext.Provider value={dataContextValue}>
         {!state.accessToken ? <Loader /> : <RouterProvider router={router} />}
      </DataContext.Provider>
   )
}

export default App
